import DemoAsset from '../components/DemoAsset';
import ReconstructionViz from '../components/ReconstructionViz';

export default function Civil3d({ onContact }) {
  return (
    <>
      <section className="bv-civil-hero">
        <div className="bv-wrap bv-hero-grid">
          <div className="bv-civil-hero-content">
            <p className="bv-eyebrow">civil3d / Spatial intelligence platform</p>
            <h1 className="bv-h1">Turn physical spaces into spatial intelligence.</h1>
            <p className="bv-lead">Capture real environments. Reconstruct them in 3D. Explore existing conditions and understand change.</p>
            <div className="bv-hero-btns">
              <a className="bv-btn bv-btn-solid" href="https://civil3d.app" target="_blank" rel="noreferrer">Explore civil3d.app</a>
              <button type="button" className="bv-btn bv-btn-ghost" onClick={onContact}>Start a conversation</button>
            </div>
          </div>
          <div className="bv-civil-hero-media">
            <DemoAsset id="hero-reconstruction" chrome="none" fallback={<ReconstructionViz variant="light" />} />
          </div>
        </div>
      </section>

      <section className="bv-civil-workflow" aria-labelledby="workflow-title">
        <div className="bv-wrap">
          <p className="bv-eyebrow">Product workflow</p>
          <h2 id="workflow-title" className="sr-only">Capture, Process, Explore, Understand</h2>
          <ol>
            {[
              ['01', 'Capture', 'Collect synchronized spatial and visual data on site.'],
              ['02', 'Process', 'Turn observations into coherent 3D geometry.'],
              ['03', 'Explore', 'Navigate, inspect and measure the reconstructed space.'],
              ['04', 'Understand', 'Document conditions and compare change over time.'],
            ].map(([n, title, text]) => <li key={n}><span>{n}</span><h3>{title}</h3><p>{text}</p><i>→</i></li>)}
          </ol>
        </div>
      </section>

      <section className="bv-civil-product-view" aria-labelledby="view-title">
        <div className="bv-wrap">
          <header className="bv-section-head bv-section-head-wide">
            <div><p className="bv-eyebrow">Spatial workspace</p><h2 id="view-title" className="bv-h2">A living view of<br />as-built reality.</h2></div>
            <p className="bv-lead">Move beyond disconnected photos and drawings into an explorable representation of the physical environment.</p>
          </header>
          <div className="bv-product-window">
            <div className="bv-window-bar"><span>civil3d / workspace</span><span>PROJECT_001 · SPATIAL VIEW</span><i /><i /><i /></div>
            <div className="bv-window-content">
              <DemoAsset id="spatial-mapping" chrome="none" fallback={<ReconstructionViz variant="light" />} />
              <aside aria-hidden="true">
                <span>SCENE LAYERS</span>
                <b>● Point cloud</b><b>○ Mesh</b><b>○ Measurements</b><b>○ Annotations</b>
                <hr />
                <span>CAPTURE STATUS</span><strong>PROCESSED</strong>
              </aside>
            </div>
          </div>
        </div>
      </section>

      <section className="bv-applications-section" aria-labelledby="applications-title">
        <div className="bv-wrap">
          <header className="bv-section-head bv-section-head-wide">
            <div><p className="bv-eyebrow">Applications</p><h2 id="applications-title" className="bv-h2">Built for the lifecycle<br />of physical projects.</h2></div>
            <p className="bv-dev-note"><i /> Active product development<br /><span>Capabilities evolve with field testing.</span></p>
          </header>
          <div className="bv-application-rows">
            {[
              ['01', 'As-built documentation', 'Create a spatial record of existing conditions that teams can revisit.'],
              ['02', 'Construction progress', 'Organize repeat captures into a clearer view of work over time.', 'In development'],
              ['03', 'Deviation + change', 'Support visual comparison of spatial conditions between captures.', 'In development'],
              ['04', 'Measurement', 'Inspect distances and geometry within reconstructed environments.', 'In development'],
            ].map(([n, title, body, status]) => <article key={n}><span>{n}</span><h3>{title}</h3><p>{body}</p><small>{status || 'Core direction'}</small></article>)}
          </div>
        </div>
      </section>

      <section className="bv-evidence-section" aria-labelledby="evidence-title">
        <div className="bv-wrap">
          <header className="bv-section-head bv-section-head-wide">
            <div><p className="bv-eyebrow">Reconstruction outputs</p><h2 id="evidence-title" className="bv-h2">From capture<br />to usable space.</h2></div>
          </header>
          <div className="bv-evidence-grid">
            <DemoAsset id="pointcloud-mesh" fallback={<CloudPlaceholder />} />
            <DemoAsset id="spatial-mapping" fallback={<ScanPlaceholder />} />
          </div>
        </div>
      </section>
    </>
  );
}

function CloudPlaceholder() {
  return (
    <div className="bv-placeholder-viz" aria-hidden="true">
      <svg viewBox="0 0 640 400" className="bv-arch-svg">
        {Array.from({ length: 80 }, (_, i) => {
          const x = 40 + (i % 16) * 36 + ((i * 7) % 11);
          const y = 60 + Math.floor(i / 16) * 56 + ((i * 13) % 17);
          return <circle key={i} cx={x} cy={y} r={i % 5 === 0 ? 2.4 : 1.2} fill="currentColor" opacity={0.2 + (i % 6) * 0.1} />;
        })}
        <text x="40" y="370" fill="currentColor" fontSize="11" opacity="0.55" fontFamily="IBM Plex Mono, monospace">POINT CLOUD / MESH</text>
      </svg>
    </div>
  );
}

function ScanPlaceholder() {
  return (
    <div className="bv-placeholder-viz" aria-hidden="true">
      <svg viewBox="0 0 640 400" className="bv-arch-svg">
        <path d="M80 300 L200 80 L360 300 Z" fill="none" stroke="currentColor" opacity="0.4" />
        <path d="M280 300 L420 100 L560 300 Z" fill="none" stroke="currentColor" opacity="0.55" />
        <line x1="40" y1="200" x2="600" y2="200" stroke="currentColor" opacity="0.25" strokeDasharray="6 8" />
        <text x="40" y="370" fill="currentColor" fontSize="11" opacity="0.55" fontFamily="IBM Plex Mono, monospace">SPATIAL MAPPING</text>
      </svg>
    </div>
  );
}
